import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import { Feather, Ionicons } from "@expo/vector-icons";
import { useDraggable, useDroppable } from "@dnd-kit/core";

import Colors from "../../../../Resources/Color";
import Tooltip from "../../../Components/Tooltip";
import { useDevice } from "../../../_layout";
import BlockCard from "../../../Block/shared/BlockCard";
import useChevronRotation from "../../../hooks/useChevronRotation";

const styles = StyleSheet.create({
  folderSection: {
    borderRadius: 12,
    transition: "background-color 160ms ease, border-color 160ms ease",
    borderWidth: 1,
    borderColor: "transparent",
    paddingHorizontal: 8,
  },
  folderSectionDropTargetValid: {
    backgroundColor: "rgba(63, 123, 141, 0.06)",
    borderColor: "rgba(63, 123, 141, 0.4)",
    borderStyle: "dashed",
  },
  folderSectionSource: {
    opacity: 0.56,
  },
  folderHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 0,
    height: 44,
  },
  folderHeaderExpanded: {},
  folderHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  folderHeaderRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginLeft: 16,
  },
  folderIconButton: {
    padding: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  folderIconButtonHovered: {
    opacity: 0.6,
  },
  folderChevronWrap: {
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  folderTitle: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "400",
    color: Colors.blackColor,
    letterSpacing: 0.2,
  },
  folderTitleInput: {
    flex: 1,
    minWidth: 0,
    height: 20,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "400",
    color: Colors.blackColor,
    letterSpacing: 0.2,
    paddingVertical: 0,
    paddingHorizontal: 0,
    borderWidth: 0,
    backgroundColor: "transparent",
    outlineStyle: "none",
  },
  folderTitleInputMobile: {
    fontSize: 16,
  },
  folderCountChip: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "rgba(30, 41, 59, 0.05)",
    alignItems: "center",
    justifyContent: "center",
  },
  folderCountText: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  folderBlocksGrid: {
    marginTop: 0,
    paddingTop: 4,
    paddingHorizontal: 0,
    paddingBottom: 14,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  folderActionDisabled: {
    opacity: 0.5,
  },
});

const DraggableBlockCard = ({
  block,
  section,
  blockDragDisabled,
  screenSize,
  i18n,
  t,
  menuItems,
  onPressBlock,
  onSummaryPress,
  onMenuAction,
  showRightIcons,
  activeBlockId,
  suppressPress,
}) => {
  const dragData = useMemo(
    () => ({
      type: "blockCard",
      blockId: block?.id,
      sourceFolderKey: section?.folderKey ?? "",
    }),
    [block?.id, section?.folderKey],
  );

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `block-card-${String(block?.id ?? "")}`,
    data: dragData,
    disabled: blockDragDisabled,
  });

  return (
    <BlockCard
      block={block}
      folderKey={section.folderKey}
      blockId={block.id}
      screenSize={screenSize}
      i18n={i18n}
      t={t}
      menuItems={menuItems}
      onPress={onPressBlock}
      onSummaryPress={onSummaryPress}
      onMenuAction={onMenuAction}
      showRightIcons={showRightIcons}
      activeBlockId={activeBlockId}
      dragAttributes={attributes}
      dragListeners={listeners}
      dragNodeRef={setNodeRef}
      isDragging={isDragging}
      dragDisabled={blockDragDisabled}
      suppressPress={suppressPress}
    />
  );
};

const FolderSection = ({
  section,
  isExpanded,
  onToggleExpand,
  onEditFolderName,
  isEditingName = false,
  editingDraft = "",
  onEditingDraftChange,
  onSaveFolderName,
  onCancelEditFolderName,
  isSavingEdit = false,
  folderRenameDisabled = false,
  onCreateBlockInFolder,
  screenSize,
  i18n,
  t,
  menuItems,
  onPressBlock,
  onSummaryPress,
  onMenuAction,
  showRightIcons = true,
  activeBlockId = null,
  blockDragDisabled = false,
  activeDragBlockId = null,
  activeDragSourceFolderKey = null,
  activeDragOverFolderKey = null,
  pressSuppressedBlockId = null,
}) => {
  const { isMobile } = useDevice();
  const folderNameInputRef = useRef(null);
  const skipNextBlurCommitRef = useRef(false);
  const pendingBlurTimerRef = useRef(null);
  const [isRenameHovered, setIsRenameHovered] = useState(false);
  const [isAddHovered, setIsAddHovered] = useState(false);
  const [isChevronHovered, setIsChevronHovered] = useState(false);
  const droppableFolderId = section.folderKey === "" ? "__general__" : section.folderKey;
  const { setNodeRef: setFolderDropRef } = useDroppable({
    id: `folder-drop-${droppableFolderId}`,
    data: {
      type: "folderDropTarget",
      folderKey: section.folderKey,
    },
    disabled: blockDragDisabled,
  });

  const isDraggingAnyBlock = activeDragBlockId != null;
  const isFolderHoveredTarget =
    isDraggingAnyBlock && activeDragOverFolderKey === section.folderKey;
  const isValidFolderTarget =
    isFolderHoveredTarget && activeDragSourceFolderKey !== section.folderKey;
  const isSourceFolder =
    isDraggingAnyBlock && activeDragSourceFolderKey === section.folderKey;
  const chevronRotation = useChevronRotation(isExpanded);

  useEffect(() => {
    if (!isEditingName) return;

    skipNextBlurCommitRef.current = false;
    if (pendingBlurTimerRef.current) {
      clearTimeout(pendingBlurTimerRef.current);
      pendingBlurTimerRef.current = null;
    }

    const id = setTimeout(() => {
      folderNameInputRef.current?.focus?.();
    }, 0);
    return () => clearTimeout(id);
  }, [isEditingName]);

  useEffect(
    () => () => {
      if (pendingBlurTimerRef.current) {
        clearTimeout(pendingBlurTimerRef.current);
        pendingBlurTimerRef.current = null;
      }
    },
    [],
  );

  const saveEdit = () => {
    if (isSavingEdit || folderRenameDisabled) return;
    skipNextBlurCommitRef.current = true;
    if (pendingBlurTimerRef.current) {
      clearTimeout(pendingBlurTimerRef.current);
      pendingBlurTimerRef.current = null;
    }
    onSaveFolderName?.();
  };

  const cancelEdit = () => {
    if (isSavingEdit) return;
    skipNextBlurCommitRef.current = true;
    if (pendingBlurTimerRef.current) {
      clearTimeout(pendingBlurTimerRef.current);
      pendingBlurTimerRef.current = null;
    }
    onCancelEditFolderName?.();
    folderNameInputRef.current?.blur?.();
  };

  const handleBlur = () => {
    if (skipNextBlurCommitRef.current) {
      skipNextBlurCommitRef.current = false;
      return;
    }

    if (pendingBlurTimerRef.current) {
      clearTimeout(pendingBlurTimerRef.current);
    }

    pendingBlurTimerRef.current = setTimeout(() => {
      pendingBlurTimerRef.current = null;
      onSaveFolderName?.();
    }, 120);
  };

  const handleNameInputKey = (e) => {
    const key = e?.nativeEvent?.key ?? e?.key;
    if (key === "Enter") {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      saveEdit();
      return;
    }

    if (key === "Escape") {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      cancelEdit();
    }
  };

  return (
    <View
      ref={setFolderDropRef}
      style={[
        styles.folderSection,
        isValidFolderTarget && styles.folderSectionDropTargetValid,
        isSourceFolder && styles.folderSectionSource,
      ]}
    >
      <TouchableOpacity
        activeOpacity={1}
        disabled={isEditingName || isDraggingAnyBlock}
        onPress={
          isEditingName ? undefined : () => onToggleExpand(section.folderKey)
        }
        style={[
          styles.folderHeader,
          isExpanded && styles.folderHeaderExpanded,
        ]}
      >
        <View style={styles.folderHeaderLeft}>
          <Ionicons name="folder-outline" size={20} color={Colors.grayColor} />
          {isEditingName ? (
            <TextInput
              ref={folderNameInputRef}
              style={[
                styles.folderTitleInput,
                isMobile && styles.folderTitleInputMobile,
              ]}
              value={editingDraft}
              onChangeText={onEditingDraftChange}
              onSubmitEditing={saveEdit}
              onBlur={handleBlur}
              onKeyDown={handleNameInputKey}
              onKeyPress={handleNameInputKey}
              blurOnSubmit={true}
              returnKeyType="done"
              numberOfLines={1}
              selectTextOnFocus
              maxLength={20}
            />
          ) : (
            <>
              <Text numberOfLines={1} style={styles.folderTitle}>
                {section.folderLabel}
              </Text>
              <View style={styles.folderCountChip}>
                <Text style={styles.folderCountText}>{section.blocks.length}</Text>
              </View>
            </>
          )}
        </View>
        <View style={styles.folderHeaderRight}>
          {isEditingName ? (
            <>
              <Tooltip content="cancel-string" placement="top">
                <TouchableOpacity
                  style={[
                    styles.folderIconButton,
                    isSavingEdit && styles.folderActionDisabled,
                  ]}
                  onPressIn={() => {
                    skipNextBlurCommitRef.current = true;
                    if (pendingBlurTimerRef.current) {
                      clearTimeout(pendingBlurTimerRef.current);
                      pendingBlurTimerRef.current = null;
                    }
                  }}
                  onPress={(e) => {
                    e?.stopPropagation?.();
                    cancelEdit();
                  }}
                  disabled={isSavingEdit}
                  activeOpacity={0.7}
                >
                  <Feather name="x" size={14} color={Colors.grayColor} />
                </TouchableOpacity>
              </Tooltip>

              <Tooltip content="save-string" placement="top">
                <TouchableOpacity
                  style={[
                    styles.folderIconButton,
                    isSavingEdit && styles.folderActionDisabled,
                  ]}
                  onPressIn={() => {
                    skipNextBlurCommitRef.current = true;
                    if (pendingBlurTimerRef.current) {
                      clearTimeout(pendingBlurTimerRef.current);
                      pendingBlurTimerRef.current = null;
                    }
                  }}
                  onPress={(e) => {
                    e?.stopPropagation?.();
                    saveEdit();
                  }}
                  disabled={isSavingEdit}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="checkmark"
                    size={14}
                    color={Colors.bubbleColor}
                  />
                </TouchableOpacity>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip content="editFolderName-string" placement="top">
                <TouchableOpacity
                  style={[
                    styles.folderIconButton,
                    isRenameHovered && styles.folderIconButtonHovered,
                    folderRenameDisabled && styles.folderActionDisabled,
                  ]}
                  onPress={(e) => {
                    e?.stopPropagation?.();
                    if (folderRenameDisabled) return;
                    onEditFolderName?.(section);
                  }}
                  onMouseEnter={() =>
                    !isMobile &&
                    !folderRenameDisabled &&
                    setIsRenameHovered(true)
                  }
                  onMouseLeave={() => setIsRenameHovered(false)}
                  disabled={folderRenameDisabled}
                  activeOpacity={0.7}
                >
                  <Feather
                    name="edit-2"
                    size={12}
                    color={Colors.darkgrayColor}
                  />
                </TouchableOpacity>
              </Tooltip>
              <Tooltip content="createBlockInFolder-string" placement="top">
                <TouchableOpacity
                  style={[
                    styles.folderIconButton,
                    isAddHovered && styles.folderIconButtonHovered,
                  ]}
                  onPress={(e) => {
                    e?.stopPropagation?.();
                    onCreateBlockInFolder?.(section);
                  }}
                  onMouseEnter={() => !isMobile && setIsAddHovered(true)}
                  onMouseLeave={() => setIsAddHovered(false)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="add" size={18} color={Colors.darkgrayColor} />
                </TouchableOpacity>
              </Tooltip>
              <Animated.View
                style={[
                  styles.folderChevronWrap,
                  isChevronHovered && styles.folderIconButtonHovered,
                  { transform: [{ rotate: chevronRotation }] },
                ]}
                onMouseEnter={() => !isMobile && setIsChevronHovered(true)}
                onMouseLeave={() => setIsChevronHovered(false)}
              >
                <Ionicons
                  name="chevron-down"
                  size={16}
                  color={Colors.darkgrayColor}
                />
              </Animated.View>
            </>
          )}
        </View>
      </TouchableOpacity>

      {isExpanded && (
        <View
          style={[
            styles.folderBlocksGrid,
            screenSize === "small" && { gap: 8 },
          ]}
        >
          {section.blocks.map((block) => (
            <DraggableBlockCard
              key={block.id}
              block={block}
              section={section}
              blockDragDisabled={blockDragDisabled}
              screenSize={screenSize}
              i18n={i18n}
              t={t}
              menuItems={menuItems}
              onPressBlock={onPressBlock}
              onSummaryPress={onSummaryPress}
              onMenuAction={onMenuAction}
              showRightIcons={showRightIcons}
              activeBlockId={activeBlockId}
              suppressPress={
                pressSuppressedBlockId === block.id || activeDragBlockId === block.id
              }
            />
          ))}
        </View>
      )}
    </View>
  );
};

export default FolderSection;
